import { get } from 'lodash';

import { config, network as networkInstance } from 'api';

import { Url } from 'lib/utils/url';
import errorHandler from 'lib/utils/errorHandler';
import { getProductsPageApiUrl, getRecommendedProductsApiUrl } from '../utils/ApiUrlGenerator';
import { extractAndDeleteProductsFromFopCollection } from 'apps/products/utils/extractAndDeleteProducts';
import { countProductsInFopCollection } from 'apps/products/utils/countProducts';
import { addProducts } from 'apps/products/actions/products';
import { changeUserViewPreference } from 'apps/user/actions/user';

export const ADD_PRODUCTS_PAGE = 'ADD_PRODUCTS_PAGE';
export const COPY_PRODUCTS_PAGE = 'COPY_PRODUCTS_PAGE';

export const SET_MAIN_FOP_COLLECTION_MAX_DISPLAY_FOPS = 'SET_MAIN_FOP_COLLECTION_MAX_DISPLAY_FOPS';

export const OPEN_MOBILE_REFINE_BY = 'OPEN_MOBILE_REFINE_BY';
export const CLOSE_MOBILE_REFINE_BY = 'CLOSE_MOBILE_REFINE_BY';

export const OPEN_FILTER_SECTION = 'OPEN_FILTER_SECTION';
export const COLLAPSE_FILTER_SECTION = 'COLLAPSE_FILTER_SECTION';

export const OPEN_CLUSTER_SECTION = 'OPEN_CLUSTER_SECTION';
export const COLLAPSE_CLUSTER_SECTION = 'COLLAPSE_CLUSTER_SECTION';

export const ADD_RECOMMENDED_FOP_COLLECTION = 'ADD_RECOMMENDED_FOP_COLLECTION';

export const VIEW_TYPE_PREFERENCE_LIST = 'LIST';
export const VIEW_TYPE_PREFERENCE_SHELF = 'SHELF';

export const SET_ENHANCED_CATALOGUE_PAGE_VISIBLE_BANNER_INDEX = 'SET_ENHANCED_CATALOGUE_PAGE_VISIBLE_BANNER_INDEX';

export const SET_RELATED_SEARCH_PRODUCTS = 'SET_RELATED_SEARCH_PRODUCTS';
export const SET_RELATED_CATEGORIES = 'SET_RELATED_CATEGORIES';

export const OPEN_QUICK_MENU = 'OPEN_QUICK_MENU';
export const CLOSE_QUICK_MENU = 'CLOSE_QUICK_MENU';

export const REGISTER_SEARCH_TERM = 'REGISTER_SEARCH_TERM';

export const INITIAL_MAX_DISPLAY_FOPS = 400; // TODO: Should be 100 for mobile devices
export const SHOW_MORE_FOPS = 100; // TODO: Should be 50 for mobile devices

const FILTER_TYPE_MAP = {
  BRAND: 'filters',
  DIETARY: 'filters',
  OPTION: 'filters',
  PRICE: 'priceFilters',
  CUSTOM_BRANDS: 'customFilters',
  CUSTOM_OPTION: 'customFilters',
  CUSTOM_DIETARY: 'customFilters',
  CUSTOM: 'customFilters',
};

export function openQuickMenu(routeString, sku, sectionId) {
  return {
    type: OPEN_QUICK_MENU,
    route: routeString,
    sku,
    sectionId,
  };
}

export function closeQuickMenu(routeString) {
  return {
    type: CLOSE_QUICK_MENU,
    route: routeString,
  };
}

export function addProductsPage(routeString, productsPage) {
  return {
    type: ADD_PRODUCTS_PAGE,
    route: routeString,
    productsPage,
  };
}

export function copyProductsPage(routeFrom, routeTo) {
  return {
    type: COPY_PRODUCTS_PAGE,
    routeFrom,
    routeTo,
  };
}

function addRecommendedFopCollection(routeString, recommendedFopCollection) {
  return {
    type: ADD_RECOMMENDED_FOP_COLLECTION,
    route: routeString,
    recommendedFopCollection,
  };
}

function fixMissingSectionFopsInfoInResponse(sections) {
  sections.forEach(section => { if (!section.fops) section.fops = []; }); // eslint-disable-line no-param-reassign
}

export function updateMainFopCollectionInfo(productsPageArg, maxDisplayFops) {
  const productsPage = productsPageArg; // reassign to local variable do eslint does not complain

  if (productsPage.mainFopCollection.sections) {
    fixMissingSectionFopsInfoInResponse(productsPage.mainFopCollection.sections);
    // count all FOPs
    productsPage.mainFopCollection.totalFops = countProductsInFopCollection(productsPage.mainFopCollection);
  } else {
    productsPage.mainFopCollection.totalFops = 0;
  }
  // display only the limited number of FOPs on the first page, so we do not blow up the browser
  productsPage.mainFopCollection.maxDisplayFops = maxDisplayFops && !Number.isNaN(maxDisplayFops)
    ? parseInt(maxDisplayFops, 10)
    : INITIAL_MAX_DISPLAY_FOPS;
}

function isOptionSelected(selectedOptions, option) {
  return selectedOptions.some(type => {
    if (type.id) {
      return type.id === option.id;
    } else {
      return type.from === option.from && type.to === option.to;
    }
  });
}

export function adjustFiltersStructure(productsPage) {
  if (get(productsPage, 'navWithFilters.filters')) {
    productsPage.navWithFilters.filters
      .filter(Boolean)
      .forEach(filter => {
        const selectedOptionsOfType = productsPage.pageParameters[FILTER_TYPE_MAP[filter.type]];
        if (selectedOptionsOfType) {
          filter.options.forEach(option => {
            option.selected = isOptionSelected(selectedOptionsOfType, option); // eslint-disable-line no-param-reassign
          });
        }
      });
  }
}

export function setMainFopCollectionMaxDisplayFops(routeString, maxDisplayFops) { // maxDisplayFops = number
  return {
    type: SET_MAIN_FOP_COLLECTION_MAX_DISPLAY_FOPS,
    route: routeString,
    maxDisplayFops,
  };
}

export function showMoreMainFopCollectionFops(routeString) {
  return (dispatch, getState) => {
    const currentProductsPage = getState().catalogue.productsPagesByRoute[routeString];
    const currentMaxDisplayFops = currentProductsPage.mainFopCollection.maxDisplayFops;
    const maxDisplayFops = currentMaxDisplayFops + SHOW_MORE_FOPS;

    // not using router by design to pass the query param, as this would cause the route change, hence
    // would request the data from server and would cause page scroll to top; update the location in history
    const currentHref = window.location.href;
    const startChar = currentHref.search('\\?') === -1 ? '?' : '&';
    const targetHref = currentHref.search('display=') === -1
      ? `${currentHref}${startChar}display=${maxDisplayFops}`
      : currentHref.replace(/display=\d+/, `display=${maxDisplayFops}`);

    // TODO could we do it some cleaner way? e.g. via react-router API ?
    window.history.replaceState(window.history.state, document.title, targetHref);

    // ... and show the additional FOPs on page
    dispatch(setMainFopCollectionMaxDisplayFops(routeString, maxDisplayFops));
  };
}

export function openMobileRefineBy(routeString) {
  return {
    type: OPEN_MOBILE_REFINE_BY,
    route: routeString,
  };
}

export function closeMobileRefineBy(routeString) {
  return {
    type: CLOSE_MOBILE_REFINE_BY,
    route: routeString,
  };
}

export function openFilterSection(routeString, filterType, filterName) {
  return {
    type: OPEN_FILTER_SECTION,
    route: routeString,
    filterType,
    filterName,
  };
}

export function collapseFilterSection(routeString, filterType, filterName) {
  return {
    type: COLLAPSE_FILTER_SECTION,
    route: routeString,
    filterType,
    filterName,
  };
}

export function openClusterSection(routeString) {
  return {
    type: OPEN_CLUSTER_SECTION,
    route: routeString,
  };
}

export function collapseClusterSection(routeString) {
  return {
    type: COLLAPSE_CLUSTER_SECTION,
    route: routeString,
  };
}

export function setEnhancedCatalogueVisibleBannerIndex(routeString, enhancedCataloguePageBannerIndex) {
  return {
    type: SET_ENHANCED_CATALOGUE_PAGE_VISIBLE_BANNER_INDEX,
    route: routeString,
    enhancedCataloguePageBannerIndex,
  };
}

export function setConvertedRelatedSearchProducts(routeString, products) {
  return {
    type: SET_RELATED_SEARCH_PRODUCTS,
    route: routeString,
    isConverted: true,
    products,
  };
}

export function setConvertedRelatedSearchCategories(routeString, categories) {
  return {
    type: SET_RELATED_CATEGORIES,
    route: routeString,
    isConverted: true,
    categories,
  };
}
export function setNonConvertedRelatedSearchProducts(routeString, products) {
  return {
    type: SET_RELATED_SEARCH_PRODUCTS,
    route: routeString,
    isConverted: false,
    products,
  };
}

export function registerSearchTerm(productsPage) {
  return {
    type: REGISTER_SEARCH_TERM,
    searchTerm: productsPage.pageParameters.searchTerm,
  };
}

export function loadProductsPage(route, buildApiUrlHelper) {
  return (dispatch, getState, { network }) => {
    const apiUrl = getProductsPageApiUrl(route, buildApiUrlHelper, getState());
    return network
      .get(apiUrl)
      .then(productsPage => {
        // search requests can be redirected to any location (configured via Kale)
        // TODO we should change the server response structure
        adjustFiltersStructure(productsPage);
        updateMainFopCollectionInfo(productsPage, route.location.query.display);

        // normalize the response (extract products)
        const products = [
          ...extractAndDeleteProductsFromFopCollection(productsPage.mainFopCollection),
          ...extractAndDeleteProductsFromFopCollection(productsPage.featuredFopCollection),
          ...extractAndDeleteProductsFromFopCollection(productsPage.sidebarFopCollection),
          ...extractAndDeleteProductsFromFopCollection(productsPage.topBarFopCollection), // add new FopCollection for Morrisons
          ...extractAndDeleteProductsFromFopCollection(productsPage.sidebarSponsoredItems), // add new Sidebar Sponsored Collection for Morrisons
        ];

        return Promise.all([
          dispatch(addProducts(products)),
          dispatch(addProductsPage(route.toString(), productsPage)),
          route.basePath === '/search'
            ? dispatch(registerSearchTerm(productsPage))
            : Promise.resolve(),
        ]);
      })
      .catch((e) => {
        errorHandler(e);
      });
  };
}

export function loadRecommendedProductsForPage(route) {
  const apiUrl = getRecommendedProductsApiUrl(route);
  return (dispatch, getState, { network }) => network
    .get(apiUrl)
    .then(recommendedFopCollection => {
      // normalize the response (extract products)
      const products = extractAndDeleteProductsFromFopCollection(recommendedFopCollection);

      return Promise.all([
        dispatch(addProducts(products)),
        dispatch(addRecommendedFopCollection(route.toString(), recommendedFopCollection)),
      ]);
    })
    .catch(errorHandler);
}

export function setViewTypePreferenceToList() {
  networkInstance.post(`${config.apiBasePath}/fop-view-preference`, {
    body: JSON.stringify({
      viewPreference: VIEW_TYPE_PREFERENCE_LIST,
    }),
  }).catch(errorHandler);
  return (dispatch) => dispatch(changeUserViewPreference(VIEW_TYPE_PREFERENCE_LIST));
}

export function setViewTypePreferenceToShelf() {
  networkInstance.post(`${config.apiBasePath}/fop-view-preference`, {
    body: JSON.stringify({
      viewPreference: VIEW_TYPE_PREFERENCE_SHELF,
    }),
  }).catch(errorHandler);
  return (dispatch) => dispatch(changeUserViewPreference(VIEW_TYPE_PREFERENCE_SHELF));
}

export function loadNonConvertedRelatedSearchProducts(routeString, searchTerm) {
  const apiUrl = new Url(`${config.apiBasePath}/search/related-search-terms/non-converted`)
    .addQueryParameter('searchTerm', searchTerm).toString();
  return (dispatch, getState, { network }) =>
    network.get(apiUrl)
      .then(products => {
        dispatch(setNonConvertedRelatedSearchProducts(routeString, products));
      }).catch(errorHandler);
}

export function loadConvertedRelatedSearchProducts(routeString, searchTerm) {
  function isBrowsePage() {
    return routeString.match(/^\/browse/);
  }

  function loadRelatedCategoriesData() {
    const apiUrl = new Url(`${config.apiBasePath}/relatedCategories`)
      .addQueryParameter('tagId', searchTerm)
      .toString();
    return { apiUrl, callback: setConvertedRelatedSearchCategories };
  }

  function loadRelatedSearchData() {
    const apiUrl = new Url(`${config.apiBasePath}/search/related-search-terms/converted`)
      .addQueryParameter('searchTerm', searchTerm)
      .toString();
    return { apiUrl, callback: setConvertedRelatedSearchProducts };
  }

  const { apiUrl, callback } = !isBrowsePage() ? loadRelatedSearchData() : loadRelatedCategoriesData();

  return (dispatch, getState, { network }) =>
    network
      .get(apiUrl)
      .then(products => dispatch(callback(routeString, products)))
      .catch(errorHandler);
}

export function ensureConvertedRelatedSearchProducts(routeString, searchTerm) {
  return (dispatch, getState) => {
    const { relatedSearch } = getState().catalogue.productsPagesByRoute[routeString] || {};
    if (!relatedSearch || !relatedSearch.isConverted) {
      dispatch(loadConvertedRelatedSearchProducts(routeString, searchTerm));
    }
  };
}
